/**
 * /admin/projetos — a lista de projetos (PROJETOS-LIMPO 2, 14/09/2026).
 *
 * Uma porta só: (1) os CINCO números do topo (esperando começar · em andamento · no prazo ·
 * atrasados · concluídos — pelo motor de prazo da ficha), (2) a seção "Esperando começar"
 * (era a página /pendentes), (3) os filtros e (4) a TABELA, como as do Financeiro; no celular,
 * cartões. Cada linha traz o prometido e a situação lidos do motor — nada é contado aqui.
 *
 * Os catálogos vivem em Catálogos; "+ Novo projeto" não existe (projeto nasce da venda em
 * Ganho — avulso não existe mais).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Tag } from 'lucide-react';

import {
  Aviso,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  KpiCard,
  Label,
  PageContainer,
  PageHeader,
  Select,
  SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { useDebounce } from '@/hooks/use-debounce';
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { useProjetos, useProjetoTipos, useProjetosPainel, type Projeto } from '@/lib/projetos-store';
import { formatDate } from '@/lib/format-date';
import { EsperandoComecar } from './EsperandoComecar';
import { atualizadoHa } from './cronograma-frases';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todas as situações' },
  { value: 'pendente', label: 'Esperando começar' },
  { value: 'em_andamento', label: 'Em andamento' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'cancelado', label: 'Cancelado' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

function statusBadge(status: string | null): { label: string; variant: 'neutral' | 'info' | 'success' | 'danger' } {
  switch (status) {
    case 'em_andamento':
      return { label: 'Em andamento', variant: 'info' };
    case 'concluido':
      return { label: 'Concluído', variant: 'success' };
    case 'bloqueado':
      return { label: 'Bloqueado', variant: 'danger' };
    // 🔴 #378: antes a cancelada caía no default e aparecia como "Esperando começar"
    case 'cancelado':
      return { label: 'Cancelado', variant: 'danger' };
    default:
      return { label: 'Esperando começar', variant: 'neutral' };
  }
}

/** A situação da linha: o prazo (motor da ficha) quando a obra anda; a situação do projeto senão. */
function situacaoDaLinha(p: Projeto): { label: string; variant: 'neutral' | 'info' | 'success' | 'danger' } {
  if (p.status === 'em_andamento' && p.prazo) {
    if (p.prazo.situacao === 'atrasado') return { label: 'Atrasado', variant: 'danger' };
    if (p.prazo.situacao === 'no-prazo') return { label: 'No prazo', variant: 'success' };
    return { label: p.prazo.rotulo, variant: 'info' };
  }
  if (p.status === 'concluido' && p.prazo) {
    if (p.prazo.situacao === 'concluido-atrasado') return { label: 'Concluído com atraso', variant: 'danger' };
    if (p.prazo.situacao === 'concluido-no-prazo') return { label: 'Concluído no prazo', variant: 'success' };
  }
  return statusBadge(p.status);
}

/**
 * 13/10 (auditoria do Cowork): a coluna "Cronograma atualizado" não repete o que a Situação já diz. Quando a Situação é
 * "Sem cronograma" (obra em andamento cujo motor do prazo diz `sem-cronograma` — a MESMA condição de `situacaoDaLinha`,
 * decidida pelo valor do motor, ❌ nunca comparando o texto do rótulo), a coluna mostra "—". Fora disso, sem importação, ela
 * diz "sem cronograma" (ex.: obra esperando começar, onde a Situação fala de outra coisa).
 */
function textoCronogramaAtualizado(p: Projeto): string {
  if (p.cronogramaAtualizado) return formatDate(p.cronogramaAtualizado.emIso);
  const situacaoJaDiz = p.status === 'em_andamento' && p.prazo?.situacao === 'sem-cronograma';
  return situacaoJaDiz ? '—' : 'sem cronograma';
}

export function ProjetosListPage() {
  const projetosV2 = useMyFeatureFlag('PROJETOS_V2_ENABLED');
  const [search, setSearch] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [status, setStatus] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const filters = useMemo(
    () => ({
      ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
      ...(tipoId && { tipoId }),
      ...(status && { status }),
    }),
    [debouncedSearch, tipoId, status],
  );

  const tiposQuery = useProjetoTipos(true);
  const projetosQuery = useProjetos(filters);
  const painel = useProjetosPainel();

  const tipos = tiposQuery.data?.items ?? [];
  const projetos = projetosQuery.data?.items ?? [];
  const n = painel.data;
  const num = (v: number | undefined) => (v === undefined ? '—' : v);

  return (
    <PageContainer>
      <PageHeader title="Projetos" subtitle="Instalações CFTV, alarme, controle de acesso e mistas — do Ganho à entrega." className="mb-4" />

      {/* Os cinco números — o mesmo motor de prazo da ficha. */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="projetos-painel">
        <KpiCard label="Esperando começar" value={num(n?.esperandoComecar)} help="Vendas ganhas que viraram projeto e ninguém iniciou ainda." data-testid="painel-esperando" />
        <KpiCard label="Em andamento" value={num(n?.emAndamento)} help="Projetos iniciados e não concluídos." data-testid="painel-andamento" />
        <KpiCard label="No prazo" value={num(n?.noPrazo)} help="Dos em andamento, quantos ainda cabem na data prometida ao cliente (início do cronograma + dias de mão de obra, em dias úteis)." data-testid="painel-no-prazo" />
        <KpiCard label="Atrasados" value={num(n?.atrasados)} help="Dos em andamento, quantos já passaram da data prometida. Obra sem cronograma não conta nem aqui nem em 'No prazo'." data-testid="painel-atrasados" />
        <KpiCard label="Concluídos" value={num(n?.concluidos)} help="Projetos concluídos, no prazo ou não." data-testid="painel-concluidos" />
      </div>

      {/* Era a página /pendentes — uma porta só (gated pela flag, como antes). */}
      {projetosV2 && (
        <div className="mb-6">
          <EsperandoComecar />
        </div>
      )}

      {/* Filtros */}
      <Card className="mb-4 p-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label htmlFor="search">Buscar</Label>
            <Input id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome ou descrição…" data-testid="filter-search" />
          </div>
          <div>
            <Label htmlFor="tipoId">Tipo</Label>
            <Select id="tipoId" value={tipoId} onChange={(e) => setTipoId(e.target.value)} data-testid="filter-tipo">
              <SelectOption value="">Todos os tipos</SelectOption>
              {tipos.map((t) => (
                <SelectOption key={t.id} value={t.id}>
                  {t.nome}
                </SelectOption>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="status">Situação</Label>
            <Select id="status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status">
              {STATUS_OPTIONS.map((opt) => (
                <SelectOption key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectOption>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {/* A lista */}
      {projetosQuery.isLoading ? (
        <ListSkeleton />
      ) : projetosQuery.isError ? (
        <Card className="p-6 text-center" data-testid="projetos-error">
          <p className="mb-3 text-red dark:text-red-glow">Não foi possível carregar os projetos.</p>
          <Button variant="ghost" onClick={() => projetosQuery.refetch()}>
            Tentar novamente
          </Button>
        </Card>
      ) : projetos.length === 0 ? (
        <EmptyState
          title="Nenhum projeto encontrado"
          description={
            Object.keys(filters).length > 0
              ? 'Tente ajustar os filtros.'
              : 'Os projetos nascem das vendas marcadas como "projeto" quando entram em Ganho no Comercial.'
          }
        />
      ) : (
        <>
          {/* Desktop: a tabela, como as do Financeiro. */}
          <Card className="hidden md:block" data-testid="projetos-tabela">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Projeto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Prometido</TableHead>
                  <TableHead>Situação</TableHead>
                  {/* 13/10 (auditoria): "Atualizado" mostrava a data em que o REGISTRO da obra mudou — qualquer edição. Passa a
                      dizer de quê: a última importação do cronograma, a MESMA do "Atualizado há N dias" da aba. */}
                  <TableHead align="right">Cronograma atualizado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projetos.map((p) => {
                  const sit = situacaoDaLinha(p);
                  return (
                    <TableRow key={p.id} data-testid={`projeto-linha-${p.id}`}>
                      <TableCell>
                        <Link to={`/admin/projetos/${p.id}`} className="font-medium text-ink-1 hover:underline dark:text-ink-dark-1">
                          {p.numeroHumano != null && <span className="mr-1 font-mono text-ink-3 dark:text-ink-dark-3">#{p.numeroHumano}</span>}
                          {p.nome}
                        </Link>
                      </TableCell>
                      <TableCell className="text-ink-2 dark:text-ink-dark-2">{p.clienteRazaoSocial ?? '—'}</TableCell>
                      <TableCell className="text-ink-2 dark:text-ink-dark-2">{p.tipoNome ?? '—'}</TableCell>
                      <TableCell className="font-mono tabular-nums">{p.prazo?.dataPrometida ? formatDate(p.prazo.dataPrometida) : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={sit.variant}>{sit.label}</Badge>
                        <SeloCronogramaParado projeto={p} />
                      </TableCell>
                      <TableCell align="right" className="font-mono text-xs tabular-nums text-ink-3 dark:text-ink-dark-3" data-testid={`projeto-cronograma-atualizado-${p.id}`}>
                        <span className={p.cronogramaAtualizado ? undefined : 'font-sans'}>{textoCronogramaAtualizado(p)}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Celular: cartões (tabela larga nunca vira rolagem de lado). */}
          <div className="grid grid-cols-1 gap-3 md:hidden" data-testid="projetos-cartoes">
            {projetos.map((p) => (
              <div key={p.id} className="space-y-1">
                <ProjetoCard projeto={p} />
                {/* fora do link: o selo abre no toque, e botão dentro de link é inválido */}
                <SeloCronogramaParado projeto={p} />
              </div>
            ))}
          </div>
        </>
      )}

      <div className="mt-4 text-xs text-ink-3 dark:text-ink-dark-3">
        {projetosQuery.data?.total ?? 0} {(projetosQuery.data?.total ?? 0) === 1 ? 'projeto' : 'projetos'}
      </div>
    </PageContainer>
  );
}

/**
 * CRONOGRAMA-MSP item 11 (09/10/2026, decidido): obra EM ANDAMENTO cujo arquivo do MS Project não entra há mais de N
 * dias (padrão 7, configurável em Configurações) ganha um selo pequeno — uma linha que abre ao clicar (regra do aviso).
 * Quem decide se está parada é o servidor (a data vem da TRILHA da importação).
 */
function SeloCronogramaParado({ projeto }: { projeto: Projeto }) {
  const c = projeto.cronogramaAtualizado;
  if (!c?.parado || c.dias == null) return null;
  return (
    <Aviso tom="atencao" fato={`Cronograma sem atualizar há ${c.dias} dias`} className="mt-1 text-xs" data-testid={`selo-cronograma-${projeto.id}`}>
      <p>{atualizadoHa(c)}</p>
      <p className="mt-1">
        Obra em andamento cujo arquivo do MS Project não entra há mais de {c.limite} dias. Reimporte o cronograma atualizado na aba
        Cronograma da obra.
      </p>
    </Aviso>
  );
}

function ProjetoCard({ projeto }: { projeto: Projeto }) {
  const sit = situacaoDaLinha(projeto);
  return (
    <Link to={`/admin/projetos/${projeto.id}`} data-testid={`projeto-card-${projeto.id}`} className="block">
      <Card className="flex h-full flex-col gap-2 p-4 transition-shadow hover:shadow-card-hover">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 flex-1 font-semibold text-ink-1 dark:text-ink-dark-1">
            {projeto.numeroHumano != null && <span className="mr-1 font-mono text-ink-3 dark:text-ink-dark-3">#{projeto.numeroHumano}</span>}
            {projeto.nome}
          </h3>
          <Badge variant={sit.variant}>{sit.label}</Badge>
        </div>
        <div className="space-y-1 text-xs text-ink-3 dark:text-ink-dark-3">
          {projeto.clienteRazaoSocial && (
            <div className="flex min-w-0 items-center gap-1">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{projeto.clienteRazaoSocial}</span>
            </div>
          )}
          {projeto.tipoNome && (
            <div className="flex min-w-0 items-center gap-1">
              <Tag className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate">{projeto.tipoNome}</span>
            </div>
          )}
          <div>
            Prometido:{' '}
            <span className="font-mono tabular-nums text-ink-1 dark:text-ink-dark-1">
              {projeto.prazo?.dataPrometida ? formatDate(projeto.prazo.dataPrometida) : '—'}
            </span>
          </div>
          <div>
            Cronograma atualizado:{' '}
            <span className="font-mono tabular-nums text-ink-1 dark:text-ink-dark-1">
              {textoCronogramaAtualizado(projeto)}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2" data-testid="projetos-loading">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="animate-pulse space-y-2 p-4">
          <div className="h-4 w-3/4 rounded bg-line-soft dark:bg-paper-dark-warm" />
          <div className="h-3 w-1/2 rounded bg-line-soft dark:bg-paper-dark-warm" />
        </Card>
      ))}
    </div>
  );
}
